import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { MoveButtons, altArrowMove, useRowDrag, DragHandle } from "./MoveButtons";
import { useFocusNewRow, enterAddsRow } from "./useFocusNewRow";

const STAGE_TYPES = [
  { value: "in_house", label: "In-House" },
  { value: "osp", label: "Outside (OSP)" },
  { value: "assembly", label: "Assembly" },
];
const firstField = (i) => `[data-testid="bom-stage-name-${i}"]`;

export function BomStagesEditor({ stages, onAdd, onChange, onRemove, onMove }) {
  const focus = useFocusNewRow(stages.length, firstField);
  const add = () => { focus.arm(); onAdd(); };
  const drag = useRowDrag(onMove);
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="bom-stages-editor" ref={focus.ref}>
      <div className="flex items-center justify-between gap-2">
        <Label>Production Stages (in Order)</Label>
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={add} data-testid="bom-add-stage">
          <Plus className="h-3 w-3 mr-1" /> Stage
        </Button>
      </div>
      {stages.length === 0 && <p className="text-[11px] text-slate-400 italic">No custom stages — orders use the default pipeline: In-House Processing → Assembly.</p>}
      {stages.map((st, i) => (
        <div key={st._key || i} data-testid={`bom-stage-row-${i}`} {...drag.row(i)} className={`flex items-center gap-2 ${drag.rowClass(i)}`} onKeyDown={altArrowMove(i, stages.length, onMove, focus.ref, firstField)}>
          <DragHandle {...drag.handle(i)} testid={`bom-stage-drag-${i}`} />
          <MoveButtons i={i} count={stages.length} onMove={onMove} prefix="bom-stage" />
          <span className="text-[10px] font-mono-data text-slate-400 w-4 text-right">{i + 1}</span>
          <Input value={st.name} onChange={(e) => onChange(i, "name", e.target.value)} onKeyDown={enterAddsRow(i === stages.length - 1, add)} placeholder="Stage name (e.g. Out for Machining)" className="flex-1 h-9 text-xs" data-testid={`bom-stage-name-${i}`} />
          <select value={st.type} onChange={(e) => onChange(i, "type", e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid={`bom-stage-type-${i}`}>
            {STAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <button type="button" onClick={() => onRemove(i)} className="text-slate-300 hover:text-red-500" data-testid={`bom-stage-remove-${i}`}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <p className="text-[10px] text-slate-400">Enter on the last row adds the next stage · use ▲▼, Alt+↑/↓ or drag the ⋮⋮ handle to reorder. 'Outside (OSP)' stages ship to vendors.</p>
    </div>
  );
}
